/**
 * TaxCollectorEmailSettingsCard (TAX-PENDING-EMAIL-001 / Phase 1b)
 * Centralized Admin configuration for the Pending → "Email" chip automation (Tax products only):
 * From Name/Email (SES-verified), single monitored Reply-To, Internal CC list, Tax Department signature,
 * global ON/OFF. Live sending cannot be enabled while readiness issues exist.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { AlertCircle, CheckCircle2, Globe, Loader2, MapPin, Phone, Send, X, XCircle } from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/formatters';
import { AutomationToggle, CheckChip, FieldLabel, PanelHeader, SettingsSection, StatusDot, StickyActionBar } from './email/EmailSettingsPrimitives';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMPTY = { enabled: false, from_name: '', from_email: '', reply_to: '', cc: [], signature: { company: '', department: '', phone: '', website: '', address: '' } };
const splitAddresses = (text) => text.split(/[;,\n\s]+/).map((s) => s.trim()).filter(Boolean);

const CcChipInput = ({ value, onChange }) => {
  const [draft, setDraft] = useState('');
  const commit = (text) => {
    const next = splitAddresses(text).filter((a) => !value.includes(a));
    if (next.length) onChange([...value, ...next]);
    setDraft('');
  };
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-200" data-testid="tax-email-cc-chips">
      {value.map((c) => (
        <span key={c} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700" data-testid="tax-email-cc-chip">
          {c}
          <button type="button" onClick={() => onChange(value.filter((a) => a !== c))} className="text-slate-400 hover:text-slate-700" aria-label={`Remove ${c}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
      <input data-testid="tax-email-cc" value={draft}
        className="min-w-[160px] flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
        placeholder={value.length ? 'Add another address' : 'Add an address and press Enter'}
        onChange={(e) => (/[;,\s]/.test(e.target.value) ? commit(e.target.value) : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
          if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => draft && commit(draft)} />
    </div>
  );
};

const SignaturePreview = ({ sig }) => {
  const lines = [sig.company, sig.department, sig.phone, sig.website, sig.address].filter(Boolean);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="tax-email-sig-preview">
      <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Live preview</div>
      {lines.length === 0 ? <p className="text-xs italic text-slate-400">Falls back to From Name</p> : (
        <div className="border-l-2 border-blue-600 pl-3 text-xs leading-5 text-slate-600">
          {sig.company && <div className="text-sm font-semibold text-slate-900">{sig.company}</div>}
          {sig.department && <div className="text-slate-700">{sig.department}</div>}
          {sig.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-slate-400" />{sig.phone}</div>}
          {sig.website && <div className="flex items-center gap-1.5 text-blue-600"><Globe className="h-3 w-3 text-slate-400" />{sig.website}</div>}
          {sig.address && <div className="flex items-center gap-1.5 text-slate-500"><MapPin className="h-3 w-3 text-slate-400" />{sig.address}</div>}
        </div>
      )}
    </div>
  );
};

export default function TaxCollectorEmailSettingsCard({ onStateChange }) {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState(null);

  const apply = (data) => {
    const next = { ...EMPTY, ...data, cc: data.cc || [], signature: { ...EMPTY.signature, ...(data.signature || {}) } };
    setForm(next);
    setSaved(next);
    setVerification(data.sender_verification || null);
    setMeta({ readiness: data.readiness, allowed: data.allowed_recipient_domains, chip: data.automation_chip, updated_at: data.updated_at, updated_by: data.updated_by });
    onStateChange?.(!!data.enabled);
  };

  useEffect(() => {
    fetch(`${API}/api/admin/settings/tax-collector-email`, { headers: authH() })
      .then((r) => r.json()).then(apply).catch(() => toast.error('Failed to load Tax Collector email settings'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSig = (k, v) => setForm((f) => ({ ...f, signature: { ...f.signature, [k]: v } }));

  const verifySender = async () => {
    setVerifying(true);
    try {
      const r = await fetch(`${API}/api/admin/settings/tax-collector-email/verify-sender`, { method: 'POST', headers: authH(), body: JSON.stringify({ email: form.from_email }) });
      const data = await r.json();
      setVerification(data);
      data.verified ? toast.success(`SES authorizes ${data.email} (${data.verified_via === 'domain_identity' ? 'domain verified' : 'address verified'})`)
        : toast.error(`SES does not authorize ${data.email || form.from_email}: ${data.reason}`);
    } catch (e) {
      toast.error('SES check failed');
    } finally {
      setVerifying(false);
    }
  };

  const save = async (enabledOverride) => {
    setSaving(true);
    try {
      const body = { ...form, enabled: enabledOverride ?? form.enabled };
      const r = await fetch(`${API}/api/admin/settings/tax-collector-email`, { method: 'PUT', headers: authH(), body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) {
        const d = data.detail;
        toast.error(typeof d === 'string' ? d : d?.message || 'Save failed');
        if (d?.issues) setMeta((m) => ({ ...m, readiness: { ready: false, issues: d.issues } }));
        return;
      }
      apply(data);
      toast.success(enabledOverride === undefined ? 'Tax Collector email settings saved' : `Automation ${body.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="flex items-center gap-2 p-6 text-sm text-slate-400" data-testid="tax-collector-email-settings-card"><Loader2 className="h-4 w-4 animate-spin" /> Loading Tax Collector email settings…</div>;
  }
  const issues = meta.readiness?.issues || [];
  const senderOk = verification?.verified && verification?.email === form.from_email?.trim().toLowerCase();
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  return (
    <div className="flex min-h-full flex-col" data-testid="tax-collector-email-settings-card">
      <PanelHeader icon={Send} iconCls="bg-indigo-50 text-indigo-600" title="Tax Collector Email Requests" subtitle="Automated tax information requests"
        right={<>
          <StatusDot tone={issues.length ? 'amber' : 'emerald'} testId="tax-email-status-badge">{issues.length ? 'Configuration Required' : 'Ready'}</StatusDot>
          <AutomationToggle checked={form.enabled}>
            <Switch data-testid="tax-email-enabled-toggle" checked={form.enabled} disabled={saving || (!form.enabled && issues.length > 0)} onCheckedChange={(v) => save(v)} />
          </AutomationToggle>
        </>} />

      <div className="px-6 pt-4">
        {issues.length > 0 ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800" data-testid="tax-email-readiness-issues">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div><span className="font-medium">Configuration required — live sending is blocked.</span>
              <ul className="ml-4 mt-0.5 list-disc space-y-0.5">{issues.map((i) => <li key={i}>{i}</li>)}</ul></div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2" data-testid="tax-email-readiness-ok">
            <CheckChip>SES Authorized</CheckChip><CheckChip>Reply-To Ready</CheckChip><CheckChip>Configuration Complete</CheckChip>
            {meta.chip?.linked && <span data-testid="tax-email-chip-linked"><CheckChip>Email Chip Linked{meta.chip.label ? `: ${meta.chip.label}` : ''}</CheckChip></span>}
            {meta.allowed?.length > 0 && (
              <span className="ml-auto inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-700" data-testid="tax-email-allowlist-chip">
                Preview: sends only to @{meta.allowed.join(', @')}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 divide-y divide-slate-100">
        <SettingsSection title="Sender" tip="Appears as the sender of every Tax Collector request. Must be authorized in Amazon SES; no-reply addresses are not allowed." testId="tax-email-section-sender">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-1.5" data-testid="tax-email-from-name-field">
              <FieldLabel required htmlFor="tax-email-from-name">From Name</FieldLabel>
              <Input id="tax-email-from-name" data-testid="tax-email-from-name" value={form.from_name} onChange={(e) => set('from_name', e.target.value)} placeholder="PNA Title Services – Tax Department" />
            </div>
            <div className="space-y-1.5" data-testid="tax-email-from-email-field">
              <FieldLabel required htmlFor="tax-email-from-email">From Email</FieldLabel>
              <div className="flex gap-2">
                <Input id="tax-email-from-email" data-testid="tax-email-from-email" value={form.from_email} onChange={(e) => { set('from_email', e.target.value); setVerification(null); }} placeholder="taxrequests@pnatitleservicesllc.com" />
                <Button type="button" variant="outline" size="sm" className="h-10 whitespace-nowrap" onClick={verifySender} disabled={verifying || !form.from_email} data-testid="tax-email-verify-sender-btn">
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check SES'}
                </Button>
              </div>
              {verification && (
                <p className={`flex flex-wrap items-center gap-1 text-[11px] ${senderOk ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="tax-email-sender-verification">
                  {senderOk ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  {senderOk ? `SES authorized · ${verification.verified_via === 'domain_identity' ? `domain ${verification.domain}` : 'verified address'}` : `Not authorized: ${verification.reason}`}
                  {verification.checked_at && <span className="text-slate-400">· {formatDateTimeLocal(verification.checked_at)}</span>}
                </p>
              )}
            </div>
          </div>
        </SettingsSection>

        <SettingsSection title="Responses & Copies" tip="Replies go to the Reply-To mailbox (email header only, never printed in the body). Internal CC recipients are visible to the Tax Collector; up to 10 addresses." testId="tax-email-section-responses">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-1.5" data-testid="tax-email-reply-to-field">
              <FieldLabel required htmlFor="tax-email-reply-to" tip="Single monitored Tax mailbox. No-reply addresses are rejected.">Reply-To Email</FieldLabel>
              <Input id="tax-email-reply-to" data-testid="tax-email-reply-to" value={form.reply_to} onChange={(e) => set('reply_to', e.target.value)} placeholder="tax@pnatitleservicesllc.com" />
            </div>
            <div className="space-y-1.5" data-testid="tax-email-cc-field">
              <FieldLabel optional tip="Press Enter, comma or semicolon to add an address. Up to 10 addresses.">Internal CC</FieldLabel>
              <CcChipInput value={form.cc} onChange={(cc) => set('cc', cc)} />
            </div>
          </div>
        </SettingsSection>

        <SettingsSection title="Tax Department Signature" tip="Closes every Tax Collector request email. Tax-history wording follows the client's MLC Tax History Requirement; each sent record freezes the From / Reply-To / CC used." testId="tax-email-section-signature">
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input data-testid="tax-email-sig-company" value={form.signature.company} onChange={(e) => setSig('company', e.target.value)} placeholder="Company" />
              <Input data-testid="tax-email-sig-department" value={form.signature.department} onChange={(e) => setSig('department', e.target.value)} placeholder="Department" />
              <Input data-testid="tax-email-sig-phone" value={form.signature.phone} onChange={(e) => setSig('phone', e.target.value)} placeholder="Phone" />
              <Input data-testid="tax-email-sig-website" value={form.signature.website} onChange={(e) => setSig('website', e.target.value)} placeholder="Website" />
              <Input data-testid="tax-email-sig-address" className="md:col-span-2" value={form.signature.address} onChange={(e) => setSig('address', e.target.value)} placeholder="Mailing address (optional)" />
            </div>
            <SignaturePreview sig={form.signature} />
          </div>
        </SettingsSection>
      </div>

      <StickyActionBar updatedAt={meta.updated_at} updatedBy={meta.updated_by} saving={saving} dirty={dirty}
        onCancel={() => setForm(saved)} onSave={() => save()} saveTestId="tax-email-save-btn" cancelTestId="tax-email-cancel-btn" barTestId="tax-email-save-bar" />
    </div>
  );
}
